import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
} from '@mui/material';
import api from 'state/axios';
import CustomDatePicker from 'components/CustomDatePicker';
import FieldDisplay from 'components/FieldDisplay';


const GENDER_OPTIONS = [
  { value: 'ML', label: 'Male' },
  { value: 'FM', label: 'Female' },
];


const CrewPersonalInfo = ({ 
  values, 
  handleChange, 
  handleBlur, 
  errors, 
  touched, 
  setFieldValue, 
  isEditMode 
}) => {
  const [airports, setAirports] = useState([]);

  useEffect(() => {
    api.get('/airports/airportswithtravelzone')
      .then((res) => setAirports(res.data || []))
      .catch(() => {});
  }, []);

  const genderLabel = GENDER_OPTIONS.find(g => g.value === values.gender)?.label || values.gender || '-';

  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Crew Code" value={values.crewCode} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 6' } }}>
        <FieldDisplay label="Crew Name" value={values.crewName} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Short Name" value={values.shortName} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Gender" value={genderLabel} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Date of Birth" value={values.birthday ? new Date(values.birthday).toLocaleDateString('en-GB') : null} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Place of Birth" value={values.placeOfBirth} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Place of Origin" value={values.placeOfOrigin} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Marital Status" value={values.marital} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Nationality" value={values.nationality} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 6' } }}>
        <FieldDisplay label="Location" value={values.location} />
      </Box>
    </Box>
  );

  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <TextField 
        fullWidth 
        size="small" 
        label="Crew Code" 
        name="crewCode"
        value={values.crewCode} 
        onChange={handleChange} 
        onBlur={handleBlur}
        error={touched.crewCode && Boolean(errors.crewCode)}
        helperText={touched.crewCode && errors.crewCode}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Crew Name" 
        name="crewName"
        value={values.crewName} 
        onChange={handleChange} 
        onBlur={handleBlur}
        error={touched.crewName && Boolean(errors.crewName)}
        helperText={touched.crewName && errors.crewName}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Short Name" 
        name="shortName"
        value={values.shortName} 
        onChange={handleChange} 
        onBlur={handleBlur}
        error={touched.shortName && Boolean(errors.shortName)}
        helperText={touched.shortName && errors.shortName}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        select 
        size="small" 
        label="Gender" 
        name="gender"
        value={values.gender || ''} 
        onChange={handleChange} 
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {GENDER_OPTIONS?.map(g => (
          <MenuItem key={g.value} value={g.value}>
            {g.label}
          </MenuItem>
        ))}
      </TextField>
      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="Date of Birth" 
        name="birthday"
        value={values.birthday ? new Date(values.birthday) : null}
        onChange={setFieldValue} 
        touched={touched} 
        errors={errors}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Place of Birth" 
        name="placeOfBirth"
        value={values.placeOfBirth} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Place of Origin" 
        name="placeOfOrigin"
        value={values.placeOfOrigin} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Marital Status" 
        name="marital"
        value={values.marital} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        size="small" 
        label="Nationality" 
        name="nationality"
        value={values.nationality} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <TextField 
        fullWidth 
        select 
        size="small" 
        label="Location" 
        name="location"
        value={values.location || ''} 
        onChange={handleChange} 
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {airports.map(a => (
          <MenuItem key={a.iataCode} value={a.iataCode}>
            {a.iataCode} - {a.airportName}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default CrewPersonalInfo;
